import type { GameId } from '@tira/protocol';
import { readPrefs } from '../lib/prefs.js';
import type { SoundView } from '../../sound/director.js';

/**
 * Ghost cards in the air.
 *
 * The boards render server state at rest: a discard IS in the pile, a meld IS
 * laid. What a table has that a render does not is the moment in between — the
 * card leaving a hand, the tiles going out at the deal. This module puts that
 * moment back as a flight layer: short-lived ghost elements that fly between
 * anchored points of the real DOM and delete themselves on landing.
 *
 * It reads the same store diffs that drive the sounds, so sight and sound hit
 * the same beat — and like the sounds, no board knows it exists. A board's
 * whole contribution is `data-fly` attributes naming its furniture: `stock`,
 * `pile`, `melds-<seat>`. The seats come free, because GameRoom's pods carry
 * `seat-<n>` for every venue at once.
 *
 * Flights are decoration, and they fail like decoration: a missing anchor
 * skips the flight, a malformed view flies nothing, and reduced motion turns
 * the whole layer off. Nothing here can block a render or a move.
 */

export interface Flight {
  from: string;
  to: string;
  kind: 'card' | 'tile';
  /** ms after the batch starts. The deal is a riffle, not a volley. */
  delay: number;
}

/* Tolerant peeks — the view is `unknown` and stays that way. */
const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : null;
const arr = (v: unknown): unknown[] | null => (Array.isArray(v) ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/** What flies at the deal, per venue. Dama and gera set up, they do not deal. */
const DEALT: Partial<Record<GameId, 'card' | 'tile'>> = {
  tongits: 'card',
  pusoydos: 'card',
  pusoy: 'card',
  mahjong: 'tile',
};

/** Cards per seat in the deal riffle — a gesture at dealing, not a census. */
const DEAL_ROUNDS = 3;
const DEAL_STEP_MS = 70;

/**
 * Diff two consecutive store states into flights. Pure; the executor below is
 * the only part that touches the DOM.
 */
/**
 * The deal riffle for a hand, from whatever state carries it. Split out of the
 * diff because the FIRST hand never crosses the diff at all: the room mounts
 * BECAUSE the match arrived, so by the time this layer subscribes, the
 * null-to-match transition is already history. The room calls this directly on
 * mount instead — gated on `seq` so resuming an old hand mid-resync does not
 * riffle a deal that happened minutes ago.
 */
export function dealPlan(state: SoundView): Flight[] {
  const n = state.match;
  if (!n) return [];
  const kind = DEALT[n.game];
  if (kind === undefined) return [];
  const seats = state.table?.seats.flatMap((s, i) => (s !== null ? [i] : [])) ?? [0, 1];
  const out: Flight[] = [];
  let i = 0;
  for (let round = 0; round < DEAL_ROUNDS; round++)
    for (const seat of seats)
      out.push({ from: 'stock', to: `seat-${seat}`, kind, delay: i++ * DEAL_STEP_MS });
  return out;
}

/** A hand young enough that its deal is the thing being looked at. */
export const FRESH_SEQ = 2;

export function flightPlan(prev: SoundView, next: SoundView): Flight[] {
  const out: Flight[] = [];
  const p = prev.match;
  const n = next.match;

  /* The deal: a new hand in a venue that deals. Once, at the start. */
  if (n && (!p || p.matchId !== n.matchId)) {
    return dealPlan(next); // a fresh hand replays nothing else
  }

  if (!p || !n || p.matchId !== n.matchId || n.seq <= p.seq) return out;

  const pv = obj(p.view);
  const nv = obj(n.view);
  if (!pv || !nv) return out;

  if (n.game === 'tongits') {
    const stockFell = (num(pv['stockCount']) ?? 0) - (num(nv['stockCount']) ?? 0) === 1;
    const discardGrew = (arr(nv['discard'])?.length ?? 0) > (arr(pv['discard'])?.length ?? 0);
    // The drawer is on the clock now; the discarder was on it a state ago.
    if (stockFell && n.turn !== null) out.push({ from: 'stock', to: `seat-${n.turn}`, kind: 'card', delay: 0 });
    if (discardGrew && p.turn !== null) out.push({ from: `seat-${p.turn}`, to: 'pile', kind: 'card', delay: 0 });
    const owner = newMeldOwner(pv, nv);
    if (owner !== null) out.push({ from: `seat-${owner}`, to: `melds-${owner}`, kind: 'card', delay: 0 });
  } else if (n.game === 'mahjong') {
    const pr = arr(pv['river'])?.length ?? 0;
    const nr = arr(nv['river']) ?? [];
    if (nr.length > pr) {
      const by = num(obj(nr[nr.length - 1])?.['by']);
      if (by !== null) out.push({ from: `seat-${by}`, to: 'pile', kind: 'tile', delay: 0 });
    } else if ((num(pv['wallCount']) ?? 0) > (num(nv['wallCount']) ?? 0) && n.turn !== null) {
      // The tile goes where the rack is: yours lies on the near rail, an
      // opponent's stands on the felt as their hold. The seat pod is only the
      // understudy, wired up in the executor's anchor fallbacks.
      const to = n.turn === n.seat ? 'rack' : `hold-${n.turn}`;
      out.push({ from: 'stock', to, kind: 'tile', delay: 0 });
    }
    const owner = newMeldOwner(pv, nv);
    if (owner !== null) out.push({ from: `seat-${owner}`, to: `melds-${owner}`, kind: 'tile', delay: 0 });
  } else if (n.game === 'pusoydos') {
    const pt = obj(pv['table']);
    const nt = obj(nv['table']);
    // A combo landed: the pile appeared, or it changed hands/cards.
    if (nt && (!pt || pt['seat'] !== nt['seat'] || arr(pt['cards'])?.length !== arr(nt['cards'])?.length)) {
      const seat = num(nt['seat']);
      if (seat !== null) out.push({ from: `seat-${seat}`, to: 'pile', kind: 'card', delay: 0 });
    }
  }

  return out;
}

/** The owner of a meld id present after but not before, if exactly that. */
function newMeldOwner(pv: Record<string, unknown>, nv: Record<string, unknown>): number | null {
  const before = new Set(
    (arr(pv['melds']) ?? []).map((m) => num(obj(m)?.['id'])).filter((x): x is number => x !== null),
  );
  for (const m of arr(nv['melds']) ?? []) {
    const id = num(obj(m)?.['id']);
    if (id !== null && !before.has(id)) return num(obj(m)?.['owner']);
  }
  return null;
}

/* ── The executor ─────────────────────────────────────────────────────────── */

/**
 * Anchor resolution, with the table itself as the understudy: a venue that
 * renders no deck still deals from somewhere, and the middle of the table is
 * where a deck would be. Seats get no fallback — a flight to nowhere in
 * particular is noise, so it is skipped instead.
 */
function anchor(root: HTMLElement, name: string): HTMLElement | null {
  const el = root.querySelector<HTMLElement>(`[data-fly="${name}"]`);
  if (el) return el;
  // A rack that is not on the rail is a seat; a hold that is not standing is
  // its seat's pod. Racks and holds are venue furniture, seats are the room's.
  if (name === 'rack') return root.querySelector<HTMLElement>('.room__rail');
  if (name.startsWith('hold-')) {
    return root.querySelector<HTMLElement>(`[data-fly="seat-${name.slice(5)}"]`);
  }
  if (name === 'stock' || name === 'pile' || name.startsWith('melds-')) {
    return root.querySelector<HTMLElement>('.room__table');
  }
  return null;
}

const FLY_MS = 380;
/** A resync can replay a burst of states; more ghosts than this is weather. */
const MAX_BATCH = 24;

export function runFlights(root: HTMLElement, flights: Flight[]): void {
  if (flights.length === 0 || readPrefs().reducedMotion) return;
  if (typeof document === 'undefined' || typeof Element.prototype.animate !== 'function') return;

  for (const f of flights.slice(0, MAX_BATCH)) {
    const fromEl = anchor(root, f.from);
    const toEl = anchor(root, f.to);
    if (!fromEl || !toEl) continue;
    const a = fromEl.getBoundingClientRect();
    const b = toEl.getBoundingClientRect();
    if (a.width === 0 || b.width === 0) continue;

    const ghost = document.createElement('i');
    ghost.className = `fly fly--${f.kind}`;
    ghost.style.left = `${a.left + a.width / 2}px`;
    ghost.style.top = `${a.top + a.height / 2}px`;
    document.body.appendChild(ghost);

    const dx = b.left + b.width / 2 - (a.left + a.width / 2);
    const dy = b.top + b.height / 2 - (a.top + a.height / 2);
    const anim = ghost.animate(
      [
        { transform: 'translate(-50%, -50%) scale(0.85)', opacity: 0.95 },
        // The midpoint lifts off the felt: a dealt card travels an arc, not a wire.
        {
          transform: `translate(calc(-50% + ${dx / 2}px), calc(-50% + ${dy / 2 - 14}px) ) scale(1.05) rotate(${dx > 0 ? 8 : -8}deg)`,
          opacity: 1,
          offset: 0.55,
        },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.9)`, opacity: 0 },
      ],
      { duration: FLY_MS, delay: f.delay, easing: 'cubic-bezier(0.3, 0.7, 0.4, 1)', fill: 'backwards' },
    );
    anim.onfinish = () => ghost.remove();
    anim.oncancel = () => ghost.remove();
  }
}
